import React from 'react';
import { useNavigate } from 'react-router-dom';
import './StaticPages.css';

const InvestorsPage = () => {
  const navigate = useNavigate();

  return (
    <div className="static-page" data-testid="investors-page"><header className="static-header">
        <div className="container">
          <div className="header-content">
            <img src="/logo.png" alt="House Sharing Seniors" className="logo-image" onClick={() => navigate('/')} />
            <nav>
              <button className="btn btn-secondary" onClick={() => navigate('/properties')}>Browse Properties</button>
              <button className="btn btn-primary" onClick={() => navigate('/apply')}>Apply Now</button>
            </nav>
          </div>
        </div>
      </header>

      <section className="page-hero" style={{ background: '#1a2332' }}>
        <div className="container">
          <h1 style={{ color: 'white' }}>Investors &amp; Government Partners</h1>
          <p style={{ color: 'rgba(255,255,255,0.8)' }}>Up to $242 return for every $1 invested in Year 1 — compounding every subsequent year at zero additional cost. The most capital-efficient social infrastructure investment in Australian aged care.</p>
        </div>
      </section>

      <section className="page-content">
        <div className="container">

          {/* The Problem */}
          <div className="content-section">
            <h2>The Problem We Solve</h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: '16px', marginTop: '16px' }}>
              {[
                { num: '924,000', label: 'Australian seniors in acute rental distress', color: '#dc2626' },
                { num: '4.2M', label: 'Total senior population — growing every year', color: '#1a2332' },
                { num: '$450+/wk', label: 'Typical pensioner rent before HSS', color: '#dc2626' },
                { num: '$674/wk', label: 'Maximum single Age Pension (with CRA)', color: '#1a2332' },
              ].map((s) => (
                <div key={s.num} style={{ padding: '20px', background: '#f8fafb', borderRadius: '10px', borderTop: `4px solid ${s.color}` }}>
                  <div style={{ fontSize: '32px', fontWeight: '900', color: s.color, lineHeight: 1 }}>{s.num}</div>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '6px', lineHeight: 1.4 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <p style={{ marginTop: '20px', color: '#4b5563', lineHeight: 1.7 }}>
              Australia has a quiet housing crisis among its most vulnerable citizens. Age Pensioners — people who have worked and contributed their entire lives — are spending 60–70% of their pension on rent alone, leaving almost nothing for food, healthcare, or dignity. Social isolation among seniors costs the Australian economy <strong>$2.7 billion per year</strong> in healthcare and social services — equivalent to smoking 15 cigarettes a day in health impact.
            </p>
          </div>

          {/* The Solution */}
          <div className="content-section">
            <h2>The HSS Solution — Group Co-Tenancy</h2>
            <p style={{ color: '#4b5563', lineHeight: 1.7, marginBottom: '20px' }}>
              House Sharing Seniors operates a professionally managed <strong>Group Co-Tenancy model</strong> — curated, vetted, compatible groups of seniors sharing premium properties. This is fundamentally different from traditional spare-room or boarding models. Each member signs an individual lease. Each qualifies independently for Government Rent Assistance (CRA). The result is dramatic.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div style={{ padding: '20px', background: '#fef2f2', borderRadius: '10px', border: '1px solid #fecaca' }}>
                <div style={{ fontWeight: '800', color: '#dc2626', marginBottom: '12px', fontSize: '15px' }}>BEFORE HSS</div>
                <div style={{ fontSize: '13px', color: '#4b5563', lineHeight: 2 }}>
                  <div>1-bed unit: ~$380/wk gross</div>
                  <div>CRA: ~$87/wk</div>
                  <div>Net out-of-pocket: <strong>~$293/wk</strong></div>
                  <div style={{ marginTop: '8px', color: '#9ca3af' }}>43% of max pension on rent alone</div>
                </div>
              </div>
              <div style={{ padding: '20px', background: '#f0fdf4', borderRadius: '10px', border: '1px solid #86efac' }}>
                <div style={{ fontWeight: '800', color: '#059669', marginBottom: '12px', fontSize: '15px' }}>WITH HSS</div>
                <div style={{ fontSize: '13px', color: '#4b5563', lineHeight: 2 }}>
                  <div>5-bed house: $1,100/wk ÷ 5 members</div>
                  <div>Share: $220/wk + same CRA $87/wk</div>
                  <div>Net out-of-pocket: <strong>~$133/wk</strong></div>
                  <div style={{ marginTop: '8px', color: '#059669', fontWeight: '700' }}>Saving: $160/wk = $8,320/year per member</div>
                </div>
              </div>
            </div>
          </div>

          {/* SROI */}
          <div className="content-section">
            <h2>Social Return on Investment (SROI)</h2>
            <p style={{ color: '#4b5563', marginBottom: '20px' }}>
              Against a $125,000 pilot investment, HSS generates extraordinary social value — and unlike most social programs, <strong>the benefit compounds every year at zero additional cost</strong>. Once a member is placed in an HSS Group Co-Tenancy, the $15,410/year social saving continues indefinitely. The initial investment is made once. The return never stops.
            </p>

            {/* Multi-year SROI table */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' }}>
              {[
                { period: 'Year 1 Floor', sroi: '$187:1', note: 'Direct rent+CRA saving only — conservative base', color: '#6b7280' },
                { period: '2% Aged-Care Scenario', sroi: '$225:1', note: 'Adds isolation, healthcare + aged-care avoidance', color: '#2563eb' },
                { period: '5% Aged-Care Scenario', sroi: '$242:1', note: 'Higher aged-care component — $20,200/member/yr', color: '#059669' },
                { period: '5-Year NPV', sroi: '~$841:1+', note: 'Zero additional investment from Year 2 onwards', color: '#dc2626' },
              ].map((s) => (
                <div key={s.period} style={{ padding: '16px', background: '#f8fafb', borderRadius: '10px', borderTop: `4px solid ${s.color}`, textAlign: 'center' }}>
                  <div style={{ fontSize: '13px', color: '#6b7280', marginBottom: '4px' }}>{s.period}</div>
                  <div style={{ fontSize: '28px', fontWeight: '900', color: s.color, lineHeight: 1 }}>{s.sroi}</div>
                  <div style={{ fontSize: '11px', color: '#9ca3af', marginTop: '6px', lineHeight: 1.4 }}>{s.note}</div>
                </div>
              ))}
            </div>

            <div style={{ background: '#1a2332', borderRadius: '12px', padding: '20px 24px', marginBottom: '20px' }}>
              <div style={{ color: '#f87171', fontWeight: '800', fontSize: '14px', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>The Key Insight</div>
              <p style={{ color: 'rgba(255,255,255,0.85)', fontSize: '15px', lineHeight: 1.7, margin: 0 }}>
                Most social programs require ongoing funding to sustain their impact. HSS does not. The $125,000 investment establishes the platform and first cohort. From that point, <strong style={{ color: 'white' }}>every year of continued tenancy generates the same $15,410 per member in social value at no further cost to the funder.</strong> Year 2 onwards is pure compounding return on the original investment.
              </p>
            </div>

            <h3 style={{ fontSize: '18px', marginBottom: '12px', color: '#1a2332' }}>Per Member Annual Social Value — 2% Aged-Care Delay Scenario</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#f8fafb' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left', borderBottom: '2px solid #e5e7eb', color: '#1a2332' }}>Benefit Component</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', color: '#1a2332' }}>Per Member / Year</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right', borderBottom: '2px solid #e5e7eb', color: '#1a2332' }}>Recurs?</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Direct rent saving + CRA ($300/wk × 52)', '$15,600', '✓ Every year'],
                    ['Social isolation cost avoidance', '$1,565', '✓ Every year'],
                    ['Healthcare reduction (44% fewer GP visits)', '$540', '✓ Every year'],
                    ['Aged care delay — 2% scenario ($1.5M ÷ 1,500)', '$1,000', '✓ Every year'],
                  ].map(([label, val, recur]) => (
                    <tr key={label} style={{ borderBottom: '1px solid #f3f4f6' }}>
                      <td style={{ padding: '10px 12px', color: '#4b5563' }}>{label}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700', color: '#1a2332' }}>{val}</td>
                      <td style={{ padding: '10px 12px', textAlign: 'right', fontSize: '12px', color: '#059669', fontWeight: '600' }}>{recur}</td>
                    </tr>
                  ))}
                  <tr style={{ background: '#f0fdf4', fontWeight: '900' }}>
                    <td style={{ padding: '12px', color: '#059669', fontSize: '15px' }}>Total — 2% aged-care delay scenario</td>
                    <td style={{ padding: '12px', textAlign: 'right', color: '#059669', fontSize: '18px' }}>≈$18,700</td>
                    <td style={{ padding: '12px', textAlign: 'right', fontSize: '12px', color: '#059669', fontWeight: '700' }}>Every year at $0 added cost</td>
                  </tr>
                </tbody>
              </table>
            </div>

            {/* Three scenarios table */}
            <h3 style={{ fontSize: '18px', margin: '24px 0 12px', color: '#1a2332' }}>SROI by Scenario — Year 1 (against $125K investment, 1,500 members)</h3>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
                <thead>
                  <tr style={{ background: '#1a2332', color: 'white' }}>
                    <th style={{ padding: '10px 12px', textAlign: 'left' }}>Scenario</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Per Member/yr</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>Total/yr</th>
                    <th style={{ padding: '10px 12px', textAlign: 'right' }}>SROI Year 1</th>
                  </tr>
                </thead>
                <tbody>
                  <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#f8fafb' }}>
                    <td style={{ padding: '10px 12px', color: '#4b5563' }}><strong>Floor</strong> — direct rent + CRA saving only</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700' }}>$15,600</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700' }}>$23.4M</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '900', color: '#2563eb', fontSize: '16px' }}>$187:1</td>
                  </tr>
                  <tr style={{ borderBottom: '1px solid #f3f4f6' }}>
                    <td style={{ padding: '10px 12px', color: '#4b5563' }}><strong>2% aged-care delay</strong> — adds isolation, healthcare + 2% aged-care avoidance</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700' }}>≈$18,700</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700' }}>$28.1M</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '900', color: '#059669', fontSize: '16px' }}>$225:1</td>
                  </tr>
                  <tr style={{ background: '#f0fdf4' }}>
                    <td style={{ padding: '10px 12px', color: '#4b5563' }}><strong>5% aged-care delay</strong> — aged-care component rises to $2,500/member</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700' }}>≈$20,200</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '700' }}>$30.3M</td>
                    <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '900', color: '#dc2626', fontSize: '16px' }}>$242:1</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p style={{ fontSize: '12px', color: '#9ca3af', marginTop: '8px' }}>
              Year 1 figures against $125K one-time investment, 1,500 members. Floor = direct rent+CRA saving only ($300/wk × 52). Upper scenarios add social isolation avoidance ($1,565/member), healthcare reduction (44% fewer GP visits, $540/member), and aged-care delay at 2% and 5% of cohort respectively. Multi-year SROI (NPV at 5%): 3-year ~$529:1, 5-year ~$841:1. Against original sunk development costs: <strong>$619:1</strong>. Full methodology available on request.
            </p>
          </div>

          {/* Funding Strategy */}
          <div className="content-section">
            <h2>Strategic Funding Roadmap</h2>

            {/* Phase 1 */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ background: '#1a2332', color: 'white', borderRadius: '20px', padding: '4px 14px', fontSize: '13px', fontWeight: '700' }}>Phase 1</div>
                <div style={{ fontWeight: '700', color: '#1a2332' }}>Foundation &amp; MVP — 0–6 Months | Target: ~$150,000</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                {[
                  { name: 'Aged Care Innovation Hub (ARIIA)', type: 'Grant', amount: '$25K–$500K+', note: 'Top priority — perfect mandate alignment' },
                  { name: 'SEFA Social Enterprise Finance', type: 'Debt/Hybrid', amount: '$25K–$250K', note: '2–4 month timeline, social enterprise fit' },
                  { name: 'R&D Tax Incentive', type: 'Tax Rebate', amount: '43.5% of eligible spend', note: 'Non-dilutive — document all platform development' },
                  { name: 'Social Impact Investment Readiness Fund', type: 'Grant', amount: '$25K–$100K', note: 'Government endorsement signal' },
                  { name: 'Remarkable Australia+ Accelerator', type: 'Program', amount: 'Mentorship + seed', note: 'Ageing tech focus — perfect fit' },
                  { name: 'MVP Ventures Program (NSW)', type: 'Grant', amount: '$25K–$75K', note: 'Monitor 2026 round opening' },
                ].map((f) => (
                  <div key={f.name} style={{ padding: '12px 14px', background: '#f8fafb', borderRadius: '8px', borderLeft: '3px solid #1a2332' }}>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#1a2332' }}>{f.name}</div>
                    <div style={{ fontSize: '12px', color: '#059669', fontWeight: '600', marginTop: '2px' }}>{f.type} — {f.amount}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{f.note}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Phase 2 */}
            <div style={{ marginBottom: '24px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ background: '#2563eb', color: 'white', borderRadius: '20px', padding: '4px 14px', fontSize: '13px', fontWeight: '700' }}>Phase 2</div>
                <div style={{ fontWeight: '700', color: '#1a2332' }}>Pilot Validation &amp; Growth — 6–18 Months | Target: $250K–$1M</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                {[
                  { name: 'Social Ventures Australia (SVA)', type: 'Impact VC', amount: '$250K–$5M', note: 'Premier social impact investor — begin relationship now' },
                  { name: 'Housing Innovation Challenge', type: 'Grant', amount: '$100K–$2M', note: 'Post-pilot data makes HSS highly competitive' },
                  { name: 'Dept of Social Services / Health', type: 'Grant', amount: '$50K–$1M', note: 'Proven outcomes in housing stress + social isolation' },
                  { name: 'Angel Investor Networks', type: 'Equity', amount: '$50K–$2M', note: 'Sydney Angels, Melbourne Angels, Scale Investors' },
                ].map((f) => (
                  <div key={f.name} style={{ padding: '12px 14px', background: '#f8fafb', borderRadius: '8px', borderLeft: '3px solid #2563eb' }}>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#1a2332' }}>{f.name}</div>
                    <div style={{ fontSize: '12px', color: '#2563eb', fontWeight: '600', marginTop: '2px' }}>{f.type} — {f.amount}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{f.note}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Phase 3 */}
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' }}>
                <div style={{ background: '#059669', color: 'white', borderRadius: '20px', padding: '4px 14px', fontSize: '13px', fontWeight: '700' }}>Phase 3</div>
                <div style={{ fontWeight: '700', color: '#1a2332' }}>National Scale-Up — 18+ Months | Target: $2M+</div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: '10px' }}>
                {[
                  { name: 'Impact Investment Group (IIG)', type: 'Impact VC', amount: '$1M–$20M', note: 'Social infrastructure & housing focus' },
                  { name: 'Reinventure / Square Peg', type: 'VC (Equity)', amount: '$1M–$20M', note: 'PropTech focus — proven scalable model' },
                  { name: 'Hostplus VC / Australian Unity', type: 'Strategic/Corp VC', amount: '$2M–$50M+', note: 'Access to member bases + property portfolios' },
                ].map((f) => (
                  <div key={f.name} style={{ padding: '12px 14px', background: '#f8fafb', borderRadius: '8px', borderLeft: '3px solid #059669' }}>
                    <div style={{ fontWeight: '700', fontSize: '14px', color: '#1a2332' }}>{f.name}</div>
                    <div style={{ fontSize: '12px', color: '#059669', fontWeight: '600', marginTop: '2px' }}>{f.type} — {f.amount}</div>
                    <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '2px' }}>{f.note}</div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="content-section" style={{ background: '#1a2332', borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
            <h2 style={{ color: 'white', marginBottom: '12px' }}>Ready to Talk?</h2>
            <p style={{ color: 'rgba(255,255,255,0.8)', marginBottom: '24px', maxWidth: '560px', margin: '0 auto 24px' }}>
              Whether you're a government agency, impact investor, grant body, or NGO — we'd welcome a conversation. Full SROI methodology, financial model, and pitch deck available on request.
            </p>
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center', flexWrap: 'wrap' }}>
              <a href="mailto:funding@housesharingseniors.com.au"
                 style={{ padding: '12px 28px', background: '#dc2626', color: 'white', borderRadius: '8px', fontWeight: '700', fontSize: '15px', textDecoration: 'none' }}>
                Government &amp; NGO Enquiry
              </a>
              <a href="mailto:funding@housesharingseniors.com.au"
                 style={{ padding: '12px 28px', background: 'white', color: '#1a2332', borderRadius: '8px', fontWeight: '700', fontSize: '15px', textDecoration: 'none' }}>
                Investor Enquiry
              </a>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '12px', marginTop: '16px' }}>
              Confidential. All SROI figures based on conservative assumptions with full methodology disclosure. Prepared June 2026.
            </p>
          </div>

        </div>
      </section>

      <footer className="static-footer">
        <div className="container">
          <p>&copy; 2026 House Sharing Seniors. All rights reserved.</p>
          <p className="footer-links">
            <a href="/privacy">Privacy Policy</a> | <a href="/terms">Terms of Service</a> | <a href="/contact">Contact Us</a>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default InvestorsPage;
